import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFileExcel, faCheck, faExclamationTriangle, faSpinner, faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import api from '../api/client';
import './YoyAmzStockPopup.css'

const YoyAmzStockPopup = ({
  showStockUploadPopup,
  setShowStockUploadPopup,
  selectedStockFile,
  setSelectedStockFile,
  parsedStockData,
  setParsedStockData,
  uploadStockStatus,
  setUploadStockStatus,
  stockFileInputRef,
  handleStockFileUpload,
  handleStockUploadSubmit,
  onSyncSuccess
}) => {
  const handleApiSync = async () => {
    try {
      setUploadStockStatus({ type: 'loading', message: 'Syncing stock from Amazon API...' });
      const { data: result } = await api.post('/api/sync-fba-stock');
      setUploadStockStatus({ type: 'success', message: result.message || 'Stock synced from Amazon API' });
      if (onSyncSuccess) onSyncSuccess();
    } catch (error) {
      setUploadStockStatus({
        type: 'error',
        message: error.response?.data?.details || error.response?.data?.error || `Error: ${error.message}`
      });
    }
  };

  const closeStockPopup = () => {
    setShowStockUploadPopup(false);
    setSelectedStockFile(null);
    setParsedStockData([]);
    setUploadStockStatus(null);
    if (stockFileInputRef.current) {
      stockFileInputRef.current.value = '';
    }
  };

  const handleFileInputClick = () => {
    if (stockFileInputRef.current) {
      stockFileInputRef.current.click();
    }
  };

  return (
    showStockUploadPopup && (
      <div className="popup-overlay">
        <div className="popup-content stock-upload-popup">
          <div className="popup-header">
            <h3>Upload Amazon FBA Stock Data</h3>
            <button className="close-btn" onClick={closeStockPopup}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="popup-body">
            {/* Синхронизация напрямую из SP-API */}
            <div className="api-sync-section">
              <button
                className="btn-upload"
                onClick={handleApiSync}
                disabled={uploadStockStatus?.type === 'loading'}
              >
                <FontAwesomeIcon icon={faCloudArrowDown} /> Sync from Amazon API
              </button>
              <span className="api-sync-hint">Pulls live FBA + AWD + stranded stock via SP-API (may take a minute). Or upload a file below:</span>
            </div>

            {/* Инструкции */}
            <div className="upload-instructions">
              <p><strong>Instructions:</strong></p>
              <ul>
                <li>Upload an Excel file (.xlsx, .xls) containing FBA stock data</li>
                <li>The file must include an <strong>ASIN</strong> column</li>
                <li>Supported columns: <code>ASIN</code>, <code>available</code>, <code>inbound-quantity</code>,
                    <code>fc-transfer</code>, <code>Reserved FC Processing</code></li>
                <li>Data will be aggregated by ASIN (sum of all warehouses)</li>
              </ul>
            </div>
            
            {/* Выбор файла */}
            <div className="file-selection">
              <input
                type="file"
                ref={stockFileInputRef}
                onChange={handleStockFileUpload}
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
              />
              
              <div className="file-selector" onClick={handleFileInputClick}>
                <div className="file-icon">
                  <FontAwesomeIcon icon={faFileExcel} />
                </div>
                <div className="file-info">
                  {selectedStockFile ? (
                    <>
                      <div className="file-name">{selectedStockFile.name}</div>
                      <div className="file-size">
                        {(selectedStockFile.size / 1024).toFixed(2)} KB
                      </div>
                    </>
                  ) : (
                    <div className="file-placeholder">
                      Click to select Excel file
                    </div>
                  )}
                </div>
              </div>
              
              <button 
                className="btn-select-file"
                onClick={handleFileInputClick}
              >
                Browse Files
              </button>
            </div>
            
            {/* Предпросмотр данных */}
            {parsedStockData.length > 0 && (
              <div className="data-preview">
                <h4>Data Preview ({parsedStockData.length} ASINs found)</h4>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>ASIN</th>
                        <th>Available</th>
                        <th>Inbound</th>
                        <th>FC Transfer</th>
                        <th>FC Processing</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedStockData.slice(0, 5).map((item, index) => (
                        <tr key={index}>
                          <td>{item.asin}</td>
                          <td>{item.available}</td>
                          <td>{item.inbound_quantity}</td>
                          <td>{item.reserved_fc_transfer}</td>
                          <td>{item.reserved_fc_processing}</td>
                          <td><strong>{item.total_quantity}</strong></td>
                        </tr>
                      ))}
                      {parsedStockData.length > 5 && (
                        <tr>
                          <td colSpan="6" className="preview-more">
                            ... and {parsedStockData.length - 5} more ASINs
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Статус загрузки */}
            {uploadStockStatus && (
              <div className={`upload-status ${uploadStockStatus.type}`}>
                <div className="status-icon">
                  {uploadStockStatus.type === 'loading' && (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  )}
                  {uploadStockStatus.type === 'success' && (
                    <FontAwesomeIcon icon={faCheck} />
                  )}
                  {uploadStockStatus.type === 'error' && (
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  )}
                </div>
                <div className="status-message">
                  {uploadStockStatus.message}
                </div>
              </div>
            )}
          </div>
          
          <div className="popup-footer">
            <button 
              className="btn-cancel"
              onClick={closeStockPopup}
            >
              Cancel
            </button>
            <button 
              className="btn-upload"
              onClick={handleStockUploadSubmit}
              disabled={!selectedStockFile || parsedStockData.length === 0 || uploadStockStatus?.type === 'loading'}
            >
              {uploadStockStatus?.type === 'loading' ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Uploading...
                </>
              ) : (
                'Upload Stock Data'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default YoyAmzStockPopup;